import api from './api';
import { auth } from '../config/firebase';
import {
  signInWithCustomToken,
  signOut as firebaseSignOut,
  User as FirebaseUser
} from 'firebase/auth';

export interface LoginCredentials {
  username: string; // accepts email (backend resolves email or username)
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  username?: string;
  location?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  error?: string;
  user?: {
    id: string;
    username: string;
    email: string;
    name: string;
    role: string;
    perfil?: string;
  };
}

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  perfil?: string;
  profile?: {
    photoURL?: string;
  };
}

class AuthService {
  /**
   * Login with username and password
   */
  async login(credentials: LoginCredentials): Promise<User> {
    try {
      // Call REST API login endpoint
      const response = await api.post<AuthResponse>('/auth/login', credentials);

      if (!response.data.success || !response.data.token || !response.data.user) {
        throw new Error(response.data.error || 'Login failed');
      }

      // Sign in to Firebase with custom token
      const customToken = response.data.token;
      await signInWithCustomToken(auth, customToken);

      return {
        ...response.data.user,
        profile: {},
      };
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<User> {
    try {
      const response = await api.post<AuthResponse>('/auth/register', data);

      if (!response.data.success || !response.data.token || !response.data.user) {
        throw new Error(response.data.error || 'Registration failed');
      }

      // Sign in to Firebase with custom token
      const customToken = response.data.token;
      await signInWithCustomToken(auth, customToken);

      return {
        ...response.data.user,
        profile: {},
      };
    } catch (error: any) {
      console.error('Register error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Registration failed');
    }
  }

  /**
   * Verify current Firebase token
   */
  async verifyToken(token: string): Promise<User> {
    try {
      const response = await api.post('/auth/verify', { token });

      if (!response.data.success) {
        throw new Error('Token verification failed');
      }

      return {
        ...response.data.user,
        profile: {},
      };
    } catch (error: any) {
      console.error('Token verification error:', error);
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  /**
   * Setup demo user (for demo environment only)
   */
  async setupDemo(secret: string = 'pagali-demo-setup-2024'): Promise<{ username: string; password: string }> {
    try {
      const response = await api.post('/auth/setup-demo', { secret });

      if (!response.data.success) {
        throw new Error('Demo setup failed');
      }

      return response.data.credentials;
    } catch (error: any) {
      console.error('Demo setup error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Demo setup failed');
    }
  }
}

export default new AuthService();
